(ns nl.mediquest.questionnaire-frontend.events
  (:require
   [ajax.core :as ajax]
   [nl.mediquest.lib.effects :as mq-lib.effects]
   [nl.mediquest.lib.router.navigation-protection :as navigation-protection]
   [nl.mediquest.questionnaire-frontend.component.i18n :as i18n]
   [nl.mediquest.questionnaire-frontend.component.modal :as component.modal]
   [nl.mediquest.questionnaire-frontend.component.questionnaire-menu :as questionnaire-menu]
   [nl.mediquest.questionnaire-frontend.component.surveyjs.model
    :refer [make-survey-model]
    :as model]
   [nl.mediquest.questionnaire-frontend.config :as config]
   [nl.mediquest.questionnaire-frontend.db :as db]
   [nl.mediquest.questionnaire-frontend.effects :as effects]
   [nl.mediquest.ui.core.modal :as modal]
   [nl.mediquest.ui.core.notifications :as notifications]
   [re-frame.core :as re-frame]))

(defn- show-error-notification [message]
  (notifications/open!
   {:notification/description message
    :notification/level :level/error}))

(defn show-info-notification [message]
  (notifications/open!
   {:notification/description message
    :notification/level :level/success}))

(defn- clear-notifications [db]
  ;; TODO: move to mq-ui via event dispatch
  (assoc-in db [:nl.mediquest.ui ::notifications/notifications] []))

(defn- survey-url [questionnaire-id questionnaire-deployment-id unsubscribe?]
  (cond-> (str config/BACKEND_HOST "/v1/" questionnaire-id)
    questionnaire-deployment-id
    (str "/" questionnaire-deployment-id)
    (and questionnaire-deployment-id unsubscribe?)
    (str "?unsubscribe=true")))

(defn- fetch-deploy-status-url [questionnaire-id questionnaire-deployment-id]
  (str config/BACKEND_HOST
       "/v1/"
       questionnaire-id
       "/"
       questionnaire-deployment-id
       "/deploy-status"))

(defn- draft-response-url [questionnaire-id questionnaire-deployment-id]
  (str config/BACKEND_HOST
       "/v1/"
       questionnaire-id
       "/"
       questionnaire-deployment-id
       "/draft-response"))

(re-frame/reg-event-fx
 ::fetch-survey
 (fn [{:keys [db]} [_]]
   (let [{:questionnaire/keys [questionnaire-id
                               questionnaire-deployment-id
                               unsubscribe?]}
         db]
     (when config/debug?
       (prn ::fetch-survey questionnaire-deployment-id))
     {:dispatch [::fetching true]
      :http-xhrio {:method :get
                   :uri (survey-url questionnaire-id questionnaire-deployment-id unsubscribe?)
                   :format (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success [::success-fetch-survey questionnaire-id]
                   :on-failure [::failure-fetch-survey]}})))

(defn- add-header-logo [db styles]
  (assoc db
         :questionnaire/header-logo-src
         (let [default-header-logo-src "/images/mq-logo.png"]
           (or (:header/logo styles) default-header-logo-src))))

(re-frame/reg-event-fx
 ::success-fetch-survey
 (fn [{{:i18n/keys [selected-locale] :as db} :db :as _cofx}
      [_ questionnaire-id {:keys [surveyjs-config draft-response]}]]
   (when config/debug?
     (prn ::success-fetch-survey {:questionnaire-id questionnaire-id}))
   (let [styles (:styles surveyjs-config)
         survey-model (make-survey-model surveyjs-config draft-response)]
     {:fx [[::effects/styling {:styles styles}]
           [::effects/set-survey-locale {:survey-model survey-model
                                         :locale selected-locale}]]
      :db (-> db
              (assoc :questionnaire/fetching? false
                     :questionnaire/survey surveyjs-config
                     :questionnaire/survey-model survey-model
                     :questionnaire/show-language-dropdown? (:show-language-dropdown surveyjs-config)
                     :questionnaire/available-locales (map keyword (:available-locales surveyjs-config)))
              (add-header-logo styles)
              (clear-notifications))})))

(re-frame/reg-event-fx
 ::fetch-survey-config
 (fn [{:keys [db]} [_]]
   (let [{:questionnaire/keys [questionnaire-id]} db]
     (when config/debug?
       (prn ::fetch-survey questionnaire-id))
     {:dispatch [::fetching true]
      :http-xhrio {:method :get
                   :uri (survey-url questionnaire-id nil nil)
                   :format (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success [::success-fetch-config-result questionnaire-id]
                   :on-failure [::failure-fetch-survey]}})))

(re-frame/reg-event-db
 ::success-fetch-config-result
 (fn [db [_ questionnaire-id response]]
   (when config/debug?
     (prn ::success-fetch-config-result {:questionnaire-id questionnaire-id}))
   (assoc db :questionnaire/config response)))

(defn- not-found? [status]
  (= status 404))

(defn- fetch-fail-message
  [status
   {:keys [error
           questionnaire/max-per-questionnaire-deployment-id]
    :as _response}]
  (if (not-found? status)
    (cond
      (or (= "inactive" error)
          (= "already-used" error)
          (and (= "max-number-filled-out" error)
               (= 1 max-per-questionnaire-deployment-id)))
      (i18n/text :notifications/already-filled-out)
      (= "max-number-filled-out" error)
      (str (i18n/text :notifications/max-number-filled-in-1)
           max-per-questionnaire-deployment-id
           (i18n/text :notifications/max-number-filled-in-2)
           (i18n/text :notifications/contact-helpdesk))
      (= "expired" error) (i18n/text :notifications/expired)
      (= "questionnaire-not-found" error)
      "De pagina die u zoekt kan niet worden gevonden."
      :else (i18n/text :notifications/unknown-code))
    (i18n/text :notifications/retrieval-error)))

(def ^:private unrecoverable-error-status-set
  #{"inactive" "already-used" "max-number-filled" "expired"})

(defn- unavailable-path
  [{:questionnaire/keys [questionnaire-id questionnaire-deployment-id] :as _db}]
  (str "/" questionnaire-id "/" questionnaire-deployment-id "/unavailable"))

(defn- fetch-fail-redirect-url
  [{:questionnaire/keys [questionnaire-id] :as db}
   {:keys [error] :as _response}]
  (cond (not questionnaire-id) "/"
        (= "questionnaire-not-found" error) "/"
        (= "unknown-questionnaire-deployment-id" error) (str "/" questionnaire-id)
        (contains? unrecoverable-error-status-set error) (unavailable-path db)
        :else "/"))

(re-frame/reg-event-fx
 ::failure-fetch-survey
 (fn [{:keys [db]}
      [_ {:keys [response status] :as _result}]]
   (when config/debug?
     (prn ::failure-fetch-survey response))
   (when (not-found? status)
     (show-error-notification (fetch-fail-message status response)))
   (let [url (fetch-fail-redirect-url db response)]
     {:db (-> db
              (merge (select-keys response [:contact-bij-vragen-hiccup]))
              (assoc :questionnaire/backend.error (not (not-found? status))
                     :questionnaire/fetching? false))
      ::mq-lib.effects/internal-redirect url})))

(re-frame/reg-event-db
 ::set-available-locales
 (fn [db [_ available-locales]]
   (assoc db :questionnaire/available-locales available-locales)))

(re-frame/reg-event-fx
 ::save-survey
 (fn [{:keys [db]} [_ survey-data]]
   (let [{:questionnaire/keys [questionnaire-id
                               questionnaire-deployment-id
                               unsubscribe?]}
         db]
     {:dispatch [::saving true]
      :http-xhrio {:method :post
                   :uri (survey-url questionnaire-id questionnaire-deployment-id unsubscribe?)
                   :params survey-data
                   :timeout 5000
                   :format (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success [::success-save-survey]
                   :on-failure [::failure-save-survey]}})))

(defn- to-report-url
  [{:questionnaire/keys [questionnaire-id questionnaire-deployment-id]} b64-signed-report-data]
  (let [plain-report-url (str "/" questionnaire-id "/" questionnaire-deployment-id "/report")]
    [::internal-redirect
     (if b64-signed-report-data
       (str plain-report-url "/" b64-signed-report-data)
       plain-report-url)]))

(defn- show-successful-save-notification []
  [::notifications/open
   {:notification/description (i18n/text :notifications/successful-save)
    :notification/level :level/success}])

(re-frame/reg-event-fx
 ::success-save-survey
 (fn [{:keys [db]} [_ {:keys [b64-signed-report-data fetch-report-data-allowed?]}]]
   (let [completed-html? (-> db :questionnaire/survey :completedHtml)]
     {:db (assoc db :questionnaire/saving? false)
      :fx [[:dispatch [::survey-dirty-status-changed false]]
           (cond
             (or fetch-report-data-allowed? b64-signed-report-data)
             [:dispatch (to-report-url db b64-signed-report-data)]
             (not completed-html?)
             [:dispatch (show-successful-save-notification)])]})))

(re-frame/reg-event-fx
 ::failure-save-survey
 (fn [{:keys [db]} [_ {:keys [response status]}]]
   (when config/debug?
     (prn ::failure-save-survey {:response response :status status}))
   (show-error-notification (i18n/text :notifications/something-wrong-save))
   {:db (assoc db :questionnaire/updating? false)
    :fx [[:dispatch [::survey-dirty-status-changed true]]
         [:dispatch [::notifications/open
                     {:notification/description
                      (i18n/text :notifications/something-wrong-save)
                      :notification/level :level/error}]]]}))

(re-frame/reg-event-fx
 ::notify-empty-response-not-send
 (fn [_cofx]
   {:fx [[:dispatch [::notifications/open
                     {:notification/description (i18n/text :notifications/empty-survey-response-not-send)
                      :notification/level :level/success}]]]}))

(re-frame/reg-event-fx
 ::save-draft
 (fn [{:keys [db]} [_ survey-data]]
   (let [{:questionnaire/keys [questionnaire-id
                               questionnaire-deployment-id]}
         db
         url (draft-response-url questionnaire-id questionnaire-deployment-id)]
     {:dispatch [::saving true]
      :http-xhrio {:method :post
                   :uri url
                   :params survey-data
                   :timeout 5000
                   :format (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success [::success-save-draft]
                   :on-failure [::failure-save-draft]}})))

(re-frame/reg-event-fx
 ::success-save-draft
 (fn [{:keys [db]} _]
   {:db (assoc db :questionnaire/saving? false)
    :fx [[:dispatch [::notifications/open
                     {:notification/description
                      (i18n/text :notifications/successful-draft-save)
                      :notification/level :level/success}]]
         [:dispatch [::survey-dirty-status-changed false]]]}))

(re-frame/reg-event-fx
 ::failure-save-draft
 (fn [{:keys [db]} [_ {:keys [response status]}]]
   (when config/debug?
     (prn ::failure-save-draft {:response response :status status}))
   {:db (assoc db :questionnaire/saving? false)
    :fx [[:dispatch [::survey-dirty-status-changed true]]
         [:dispatch [::notifications/open
                     {:notification/description
                      (i18n/text :notifications/something-wrong-save)
                      :notification/level :level/error}]]]}))

(re-frame/reg-event-fx
 ::survey-dirty-status-changed
 (fn [_ [_ dirty?]]
   (when config/debug?
     (prn ::survey-dirty-status-changed dirty?))
   {:fx [[::effects/call-fn #(model/make-draft-button-visible dirty?)]
         [:dispatch [::navigation-protection/protect dirty?]]]}))

(defn- verify-integrity-report-url-path-url
  [questionnaire-id questionnaire-deployment-id]
  (str config/BACKEND_HOST
       "/v1/"
       questionnaire-id
       "/"
       questionnaire-deployment-id
       "/verify-integrity-report-url-path"))

(re-frame/reg-event-fx
 ::verify-integrity-report-url-path
 (fn [{:keys [db]} [_ {:report/keys [url-path]
                       :questionnaire/keys [questionnaire-id
                                            questionnaire-deployment-id]}]]
   {:db (assoc db
               :questionnaire/questionnaire-id
               questionnaire-id
               :questionnaire/questionnaire-deployment-id
               questionnaire-deployment-id)
    :http-xhrio {:method :post
                 :uri (verify-integrity-report-url-path-url questionnaire-id
                                                            questionnaire-deployment-id)
                 :params {:url-path url-path}
                 :timeout 5000
                 :format (ajax/json-request-format)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success [::success-verify-integrity-report-url-path]
                 :on-failure [::failure-verify-integrity-report-url-path]}}))

(defn- assoc-active-panel
  [db panel]
  (assoc db :active-panel panel))

(re-frame/reg-event-db
 ::success-verify-integrity-report-url-path
 (fn [db [_ {:keys [verified-report-data] :as _response}]]
   (-> db
       (assoc-active-panel :result-panel)
       (assoc :questionnaire/report-data verified-report-data))))

(defn- show-invalid-report-url-notification []
  [::notifications/open
   {:notification/description
    (i18n/text :notification/invalid-url)
    :notification/level :level/error}])

(re-frame/reg-event-fx
 ::failure-verify-integrity-report-url-path
 (fn [_ _]
   {:fx [[:dispatch [::internal-redirect "/"]]
         [:dispatch (show-invalid-report-url-notification)]]}))

(defn- report-data-url
  [questionnaire-id questionnaire-deployment-id]
  (str config/BACKEND_HOST
       "/v1/"
       questionnaire-id
       "/"
       questionnaire-deployment-id
       "/report-data"))

(re-frame/reg-event-fx
 ::fetch-report-data
 (fn [{:keys [db]} [_ {:questionnaire/keys [questionnaire-id
                                            questionnaire-deployment-id]}]]
   {:db (assoc db
               :questionnaire/questionnaire-id
               questionnaire-id
               :questionnaire/questionnaire-deployment-id
               questionnaire-deployment-id)
    :http-xhrio {:method :get
                 :uri (report-data-url questionnaire-id questionnaire-deployment-id)
                 :timeout 5000
                 :format (ajax/json-request-format)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success [::success-fetch-report-data]
                 :on-failure [::failure-fetch-report-data]}}))

(re-frame/reg-event-db
 ::success-fetch-report-data
 (fn [db [_ {:keys [report-data] :as _response}]]
   (-> db
       (assoc-active-panel :result-panel)
       (assoc :questionnaire/report-data report-data))))

(re-frame/reg-event-fx
 ::failure-fetch-report-data
 (fn [_ _]
   {:fx [[:dispatch [::internal-redirect "/"]]
         [:dispatch (show-invalid-report-url-notification)]]}))

(re-frame/reg-event-db
 ::fetching
 (fn [db [_ fetching?]]
   (assoc db :questionnaire/fetching? fetching?)))

(re-frame/reg-event-db
 ::saving
 (fn [db [_ saving?]]
   (assoc db :questionnaire/saving? saving?)))

(re-frame/reg-event-fx
 ::fetch-deploy-status
 (fn [{:keys [db]} [_]]
   (let [{:questionnaire/keys [questionnaire-id
                               questionnaire-deployment-id]}
         db]
     (when config/debug?
       (prn ::fetch-deploy-status questionnaire-id questionnaire-deployment-id))
     {:dispatch [::fetching true]
      :http-xhrio {:method :get
                   :uri (fetch-deploy-status-url questionnaire-id questionnaire-deployment-id)
                   :format (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success [::success-fetch-deploy-status questionnaire-id]
                   :on-failure [::failure-fetch-deploy-status]}})))

(re-frame/reg-event-db
 ::success-fetch-deploy-status
 (fn [db [_ questionnaire-id {:keys [deploy-status response]}]]
   (when config/debug?
     (prn ::success-fetch-deploy-status {:questionnaire-id questionnaire-id :response response}))
   (assoc db
          :questionnaire/fetching? false
          :questionnaire/deploy-status deploy-status)))

(defn- fetch-deploy-status-error-message
  [status]
  (if (not-found? status)
    "Deze vragenlijstcode is niet bekend."
    "Er is iets misgegaan bij het ophalen van de status van de vragenlijstcode."))

(re-frame/reg-event-fx
 ::failure-fetch-deploy-status
 (fn [{:keys [db]}
      [_ {:keys [response status] :as _result}]]
   (when config/debug?
     (prn ::failure-fetch-deploy-status response))
   (show-error-notification (fetch-deploy-status-error-message status))
   {:db (assoc db :questionnaire/fetching? false)
    ::mq-lib.effects/internal-redirect "/"}))

(re-frame/reg-event-fx
 ::internal-redirect
 (fn [_ [_ url]]
   (when config/debug?
     (prn ::internal-redirect url))
   {::mq-lib.effects/internal-redirect url}))

(re-frame/reg-event-db
 ::prepare-home-panel
 (fn [db [_]]
   (assoc-active-panel db :home-panel)))

(re-frame/reg-event-db
 ::prepare-landingspage-panel
 (fn [db [_ {:questionnaire/keys [questionnaire-id]}]]
   (-> db
       (assoc :questionnaire/questionnaire-id questionnaire-id)
       (assoc-active-panel :landingspage-panel))))

(re-frame/reg-event-fx
 ::prepare-questionnaire-panel
 (fn [{:keys [db]}
      [_ {:questionnaire/keys [questionnaire-deployment-id questionnaire-id questionnaire-title]
          :as _route-params} & {:keys [unsubscribe?]}]]
   {:db (-> db
            (dissoc :questionnaire/survey)
            (assoc :questionnaire/questionnaire-deployment-id
                   questionnaire-deployment-id
                   :questionnaire/questionnaire-id
                   questionnaire-id
                   :questionnaire/questionnaire-title
                   questionnaire-title
                   :questionnaire/unsubscribe?
                   unsubscribe?)
            (assoc-active-panel :questionnaire-panel))
    :dispatch [::fetch-survey]}))

(re-frame/reg-event-fx
 ::prepare-deployment-unavailable-panel
 (fn [{:keys [db]}
      [_ {:questionnaire/keys [questionnaire-deployment-id questionnaire-id questionnaire-title]
          :as _route_params}]]
   {:db (-> db
            (assoc :questionnaire/questionnaire-deployment-id
                   questionnaire-deployment-id
                   :questionnaire/questionnaire-id
                   questionnaire-id
                   :questionnaire/questionnaire-title
                   questionnaire-title)
            (assoc-active-panel :deployment-unavailable-panel))
    :dispatch [::fetch-deploy-status]}))

(re-frame/reg-event-fx
 ::prepare-deploy-status-panel
 (fn [{:keys [db]}
      [_ {:questionnaire/keys [questionnaire-deployment-id questionnaire-id questionnaire-title]
          :as _route_params}]]
   {:db (-> db
            (assoc :questionnaire/questionnaire-deployment-id
                   questionnaire-deployment-id
                   :questionnaire/questionnaire-id
                   questionnaire-id
                   :questionnaire/questionnaire-title
                   questionnaire-title)
            (assoc-active-panel :deploy-status-panel))
    :dispatch [::fetch-deploy-status]}))

(re-frame/reg-event-fx
 ::prepare-result-panel
 (fn [{:keys [db]} [_]]
   (if (seq (:questionnaire/survey-data db))
     {:db (assoc-active-panel db :result-panel)}
     {::mq-lib.effects/internal-redirect "/"})))

(re-frame/reg-event-db
 ::prepare-active-panel
 (fn [db [_ panel]]
   (assoc-active-panel db panel)))

(re-frame/reg-event-fx
 ::prepare-menu-panel
 (fn [{:keys [db]} [_ panel {:questionnaire/keys [menu-id questionnaire-deployment-id] :as route-params}]]
   {:db (assoc
         (assoc-active-panel db panel)
         :questionnaire/menu-id menu-id
         :questionnaire/questionnaire-deployment-id (not-empty questionnaire-deployment-id))
    :fx [[:dispatch [::questionnaire-menu/init route-params]]]}))

(re-frame/reg-event-fx
 ::new-active-panel
 (fn [_ [_ active-panel route-params]]
   {:dispatch (condp = active-panel
                :questionnaire-panel
                [::prepare-questionnaire-panel route-params]
                :unsubscribe-panel
                [::prepare-questionnaire-panel route-params :unsubscribe? true]
                :deployment-unavailable-panel
                [::prepare-deployment-unavailable-panel route-params]
                :deploy-status-panel
                [::prepare-deploy-status-panel route-params]
                :result-panel
                (if (:report/url-path route-params)
                  [::verify-integrity-report-url-path route-params]
                  [::fetch-report-data route-params])
                :menu-panel
                [::prepare-menu-panel :menu-panel route-params]
                :privacy-panel
                [::prepare-active-panel :privacy-panel]
                :bad-url-panel
                [::internal-redirect "/"]
                :landingspage-panel
                [::prepare-landingspage-panel route-params]
                [::prepare-home-panel])}))

(re-frame/reg-event-db
 ::initialize-db
 (fn [_ _]
   db/default-db))

(re-frame/reg-cofx
 ::window-location-origin
 (fn [cofx _]
   (assoc cofx :window-location-origin js/window.location.origin)))

(re-frame/reg-event-fx
 ::base-url
 [(re-frame/inject-cofx ::window-location-origin)]
 (fn [{:keys [db window-location-origin]}]
   {:db (assoc db :questionnaire/base-url window-location-origin)}))

(defn questionnaire-backend-email-report-url
  [questionnaire-id questionnaire-deployment-id]
  (-> (str config/BACKEND_HOST
           "/v1/"
           questionnaire-id
           "/"
           questionnaire-deployment-id
           "/email-report")))

(re-frame/reg-event-fx
 ::success-email-report
 (fn [{:keys [db]}]
   {:db (dissoc db :report/show-email-button?)
    :dispatch [::notifications/open
               {:notification/description
                (i18n/text :notification/email-sent-successfully)
                :notification/level :level/success}]}))

(re-frame/reg-event-fx
 ::failure-email-report
 (fn [_]
   {:dispatch [::notifications/open
               {:notification/description
                (i18n/text :notification/something-wrong-emailing)
                :notification/level :level/error}]}))

(re-frame/reg-cofx
 ::window-location-href
 (fn [cofx _]
   (assoc cofx :window-location-href js/window.location.href)))

(re-frame/reg-event-fx
 ::email-report
 [(re-frame/inject-cofx ::window-location-href)]
 (fn [{:keys [db window-location-href]} [_ email-address]]
   (let [{:questionnaire/keys [questionnaire-id
                               questionnaire-deployment-id]} db]
     {:http-xhrio
      {:method :post
       :uri (questionnaire-backend-email-report-url questionnaire-id
                                                    questionnaire-deployment-id)
       :params {:email-address email-address
                :report-url window-location-href}
       :timeout 5000
       :format (ajax/json-request-format)
       :response-format (ajax/raw-response-format)
       :on-success [::success-email-report]
       :on-failure [::failure-email-report]}
      :dispatch [::modal/set-modal nil]})))

(re-frame/reg-event-fx
 ::set-locale
 (fn [{{:i18n/keys [locales]
        :questionnaire/keys [survey-model]
        :as db} :db}
      [_ locale]]
   (when-not (contains? (set locales) locale)
     (prn "locale not available" {:selected-locale locale :locales locales}))
   {:db (assoc db :i18n/selected-locale locale)
    :fx [[::effects/set-survey-locale {:survey-model survey-model
                                       :locale locale}]]}))

(re-frame/reg-event-db
 ::locale-dropdown-open?
 (fn [db [_ open?]]
   (assoc db :i18n/locale-dropdown-open? open?)))

(re-frame/reg-event-fx
 ::navigation-protection/triggered
 (fn [_ [_ destination]]
   {:fx [[::modal/open
          {:modal
           (fn []
             [component.modal/confirm-leave-modified-questionnaire-modal
              {:on-confirm #(re-frame/dispatch
                             [::navigation-protection/leave-to-destination destination])}])}]]}))
